import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export interface TrustedContact {
  id: string;
  user_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  relationship: string | null;
  notify_on_press: boolean;
  notify_on_missed_checkin: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const TRUSTED_CIRCLE_MAX = 5;

export function useTrustedCircle() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<TrustedContact[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setContacts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await (supabase as any)
      .from("trusted_circle")
      .select("*")
      .eq("user_id", user.id)
      .order("sort_order", { ascending: true });
    setContacts((data as TrustedContact[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = useCallback(
    async (contact: Omit<TrustedContact, "id" | "user_id" | "created_at" | "updated_at" | "sort_order">) => {
      if (!user) return { error: "not_signed_in" };
      if (contacts.length >= TRUSTED_CIRCLE_MAX) {
        return { error: `Capped at ${TRUSTED_CIRCLE_MAX} contacts.` };
      }
      const { error } = await (supabase as any).from("trusted_circle").insert({
        ...contact,
        user_id: user.id,
        sort_order: contacts.length,
      });
      if (error) return { error: error.message };
      await load();
      return { error: null };
    },
    [user, contacts, load],
  );

  const update = useCallback(
    async (id: string, patch: Partial<TrustedContact>) => {
      const { error } = await (supabase as any)
        .from("trusted_circle")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) return { error: error.message };
      await load();
      return { error: null };
    },
    [load],
  );

  const remove = useCallback(
    async (id: string) => {
      const { error } = await (supabase as any).from("trusted_circle").delete().eq("id", id);
      if (error) return { error: error.message };
      await load();
      return { error: null };
    },
    [load],
  );

  return { contacts, loading, add, update, remove, reload: load };
}
